import {useQueries, useQueryClient} from '@tanstack/react-query';
import clsx from 'clsx';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Box, Stack} from '@mui/material';
import {
  Body,
  Headline,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import aggregateActivityFeedItems, {
  FEED_ITEM_TYPE,
} from '@/utils/aggregateActivityFeedItems';
import aggregateCreatorFeedItems from '@/utils/aggregateCreatorFeedItems';
import {moveContentSubmissionInCache} from '@/utils/cache-updaters/contentSubmissions';
import extractFirstName from '@/utils/extractFirstName';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {
  CONTENT_SUBMISSION_STATUS,
  ContentSubmission,
  ContentSubmissionVersion,
} from '@/types/contentSubmission';
import {ConversationMessage} from '@/types/conversationMessage';
import contentSubmissionVersionMessagesFetchers from '@/api/fetchers/contentSubmissionVersionMessages';
import ConfirmModal from '@/components/confirm-modal';
import SegmentedControl from '@/components/segmented-control';
import useSendVersionMessage from '@/hooks/mutations/content-submission-versions/use-send-version-message';
import useContentSubmissionTransitionMutation from '@/hooks/mutations/use-content-submission-transition-mutation';
import useCreateCommentMutation from '@/hooks/mutations/use-create-comment';
import {CONTENT_SUBMISSION_QUERY_KEY} from '@/hooks/queries/use-content-submission-query';
import {CONTENT_SUBMISSION_VERSION_MESSAGES_QUERY_KEY} from '@/hooks/queries/use-content-submission-version-messages';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useMessageAttachments from '@/hooks/use-message-attachments';
import useMessagingPanel from '@/hooks/use-messaging-panel';
import CommentInput from './CommentInput';
import sharedStyles from './ContentSubmissionActivityFeed.module.scss';
import FeedItemView from './FeedItemView';
import styles from './OnPlatformActivityFeed.module.scss';
import RequestEditsInterceptModal from './RequestEditsInterceptModal';
import {
  setSkipRequestEditsMessageIntercept,
  shouldShowRequestEditsMessageIntercept,
} from './skipRequestEditsMessageIntercept';

const TAB_INTERNAL = 'internal';
const TAB_CREATOR = 'creator';

const SEGMENT_DEFS = [
  {id: TAB_INTERNAL, localeLabelKey: 'Internal'},
  {id: TAB_CREATOR, localeLabelKey: 'Creator'},
];

interface OnPlatformActivityFeedProps {
  testID?: string;
  version: ContentSubmissionVersion;
  contentSubmission: ContentSubmission;
  onCommentSubmit?: () => void;
  onActivityTabChange?: (tab: string) => void;
}

function OnPlatformActivityFeed({
  testID = 'content-submission-activity-feed',
  version,
  contentSubmission,
  onCommentSubmit,
  onActivityTabChange,
}: OnPlatformActivityFeedProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {open: openMessagingPanel} = useMessagingPanel();
  const activeTab =
    searchParams.get('activityFeed') === TAB_CREATOR
      ? TAB_CREATOR
      : TAB_INTERNAL;
  const setActiveTab = (tab: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === TAB_CREATOR) {
          next.set('activityFeed', TAB_CREATOR);
        } else {
          next.delete('activityFeed');
        }
        return next;
      },
      {replace: true}
    );

  // Internal tab state
  const [commentText, setCommentText] = useState('');
  const {dashboardUser} = useDashboardUserQuery({});
  const {mutate: createComment, isLoading: isSubmitting} =
    useCreateCommentMutation({contentSubmissionId: contentSubmission.id});

  const internalFeedListRef = useRef<HTMLDivElement>(null);
  const creatorFeedListRef = useRef<HTMLDivElement>(null);

  // Creator tab state
  const [creatorMessageText, setCreatorMessageText] = useState('');
  // Confirmation modal for the creator-message send flow. We surface this
  // modal before actually firing the mutation so users can't accidentally
  // send an external-facing message with a single click.
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  // When the asset is awaiting a brand decision, intercept send and offer
  // Request Edits (formal revision) vs Send as message.
  const [interceptOpen, setInterceptOpen] = useState(false);
  const versionIds = useMemo(
    () => contentSubmission.versions?.map((v) => v.id) ?? [],
    [contentSubmission.versions]
  );
  const versionMessageQueries = useQueries({
    queries: versionIds.map((id) => ({
      queryKey: [CONTENT_SUBMISSION_VERSION_MESSAGES_QUERY_KEY, id],
      queryFn: () => contentSubmissionVersionMessagesFetchers.list(id),
      staleTime: 30 * 1000,
      refetchOnWindowFocus: true,
    })),
  });
  const allVersionMessages = useMemo(() => {
    const map: Record<string, ConversationMessage[]> = {};
    versionIds.forEach((id, i) => {
      map[id] = (versionMessageQueries[i].data as ConversationMessage[]) ?? [];
    });
    return map;
  }, [versionIds, versionMessageQueries]);
  const isCreatorMessagesLoading =
    versionIds.length > 0 &&
    versionMessageQueries.some((query) => query.isLoading);
  const queryClient = useQueryClient();
  const sendVersionMessage = useSendVersionMessage();
  const {mutate: submitRejectTransition, isLoading: isRejecting} =
    useContentSubmissionTransitionMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CONTENT_SUBMISSION_QUERY_KEY, contentSubmission.id],
        });
      },
    });
  const isSendingRef = useRef(false);
  const campaignId = contentSubmission.campaign?.id ?? '';
  const {
    pendingAttachments,
    isUploading,
    handleFileChange,
    handleRemoveAttachment,
    sendAttachments,
    reset: resetPendingAttachments,
  } = useMessageAttachments();

  const isLatestVersion = version.id === contentSubmission.latestVersionId;
  const latestVersion = isLatestVersion
    ? version
    : (contentSubmission.versions?.find(
        (v) => v.id === contentSubmission.latestVersionId
      ) ?? version);

  const brandName =
    contentSubmission.campaign?.brand?.displayName ??
    contentSubmission.campaign?.brand?.name ??
    '';
  const brandId = contentSubmission.campaign?.brand?.id ?? '';
  const creatorProfileId = contentSubmission.profile?.id ?? null;
  const creatorName = contentSubmission.profile?.displayName ?? '';
  const creatorFirstName = extractFirstName(creatorName);

  const feedItems = useMemo(
    () => aggregateActivityFeedItems(contentSubmission),
    [contentSubmission]
  );

  const creatorFeedItems = useMemo(
    () => aggregateCreatorFeedItems(contentSubmission, allVersionMessages),
    [contentSubmission, allVersionMessages]
  );
  // Upload / approval pills are not the message thread. Empty copy is for
  // "no messages on this asset", so it still shows when those events exist.
  const hasCreatorThreadMessages = creatorFeedItems.some(
    (item) =>
      item.type === FEED_ITEM_TYPE.VERSION_MESSAGE ||
      item.type === FEED_ITEM_TYPE.BRAND_MESSAGE
  );
  const showCreatorThreadEmpty =
    !isCreatorMessagesLoading && !hasCreatorThreadMessages;

  // Chat-style ordering: items are sorted oldest -> newest, so we keep the
  // scroll position pinned to the bottom whenever the feed length changes
  // (initial load, new comments, version updates, tab switches, etc.).
  useEffect(() => {
    internalFeedListRef.current?.scrollTo({
      top: internalFeedListRef.current.scrollHeight,
    });
  }, [feedItems.length, activeTab]);

  useEffect(() => {
    creatorFeedListRef.current?.scrollTo({
      top: creatorFeedListRef.current.scrollHeight,
    });
  }, [creatorFeedItems.length, activeTab]);

  const segments = useMemo(
    () =>
      SEGMENT_DEFS.map((segment) => ({
        ...segment,
        label: translate(segment.localeLabelKey),
      })),
    []
  );

  const commenterName =
    dashboardUser.displayName ||
    [dashboardUser.firstName, dashboardUser.lastName]
      .filter(Boolean)
      .join(' ') ||
    dashboardUser.email ||
    '';

  const commenterEmail = dashboardUser.email || '';

  const handleSubmitComment = () => {
    const trimmed = commentText.trim();
    if (!trimmed || isSubmitting) {
      return;
    }

    onCommentSubmit?.();
    createComment(
      {
        content: trimmed,
        email: commenterEmail,
        fullName: commenterName,
        contentSubmissionVersion: {
          id: latestVersion.id,
          contentSubmissionComments:
            latestVersion.contentSubmissionComments ?? [],
        },
      },
      {
        onSuccess: () => {
          setCommentText('');
          internalFeedListRef.current?.scrollTo({
            top: internalFeedListRef.current.scrollHeight,
            behavior: 'smooth',
          });
        },
      }
    );
  };

  // Validate, then open a modal — do not send yet. If the asset is still
  // awaiting a brand decision, show the Request Edits intercept; otherwise
  // show the usual "Send to creator?" confirmation.
  const handleCreatorMessageSubmit = () => {
    const trimmed = creatorMessageText.trim();
    // Second-line guard: ChatInput already checks canSend before onSubmit.
    if (!trimmed || isSendingRef.current || isUploading) {
      return;
    }
    if (
      shouldShowRequestEditsMessageIntercept({
        aasmState: contentSubmission.aasmState,
        isLatestVersion,
      })
    ) {
      setInterceptOpen(true);
      return;
    }
    setConfirmSendOpen(true);
  };

  // Step 2: Actually send the creator message. Invoked from ConfirmModal or
  // from the intercept modal's "Send as message" CTA. The `finally` block
  // closes modals on both success and failure; composer text is only cleared
  // on success, so cancelled / failed sends keep the user's draft intact.
  const executeSendCreatorMessage = async () => {
    const trimmed = creatorMessageText.trim();
    if (isSendingRef.current) {
      return;
    }

    isSendingRef.current = true;
    onCommentSubmit?.();
    try {
      const newMessage = await sendVersionMessage.mutateAsync({
        versionId: latestVersion.id,
        body: trimmed,
        sentFromBrandId: brandId,
      });
      // sendAttachments resets pendingAttachments internally via its finally block.
      await sendAttachments(newMessage.id);
      // Re-invalidate after attachments are created — the first invalidation (from
      // useSendVersionMessage's onSuccess) fires before attachments exist on the server.
      queryClient.invalidateQueries({
        queryKey: [
          CONTENT_SUBMISSION_VERSION_MESSAGES_QUERY_KEY,
          latestVersion.id,
        ],
      });
      setCreatorMessageText('');
      creatorFeedListRef.current?.scrollTo({
        top: creatorFeedListRef.current.scrollHeight,
        behavior: 'smooth',
      });
    } catch {
      // error handled by React Query / useMessageAttachments
    } finally {
      isSendingRef.current = false;
      setConfirmSendOpen(false);
      setInterceptOpen(false);
    }
  };

  const persistDontAskAgain = (dontAskAgain: boolean) => {
    if (dontAskAgain) {
      setSkipRequestEditsMessageIntercept(true);
    }
  };

  // Submit the formal edit request immediately with the composer draft — the
  // user already wrote their feedback, so opening BrandFeedbackMessageModal
  // again would be a redundant second confirm.
  const handleInterceptRequestEdits = (dontAskAgain: boolean) => {
    const brandMessage = creatorMessageText.trim();
    if (!brandMessage || isRejecting) {
      return;
    }
    persistDontAskAgain(dontAskAgain);
    if (campaignId) {
      moveContentSubmissionInCache(
        campaignId,
        contentSubmission.id,
        CONTENT_SUBMISSION_STATUS.PENDING,
        CONTENT_SUBMISSION_STATUS.REJECTED
      );
    }
    submitRejectTransition(
      {
        action: 'reject',
        contentSubmissionId: contentSubmission.id,
        campaignId,
        brandMessage,
      },
      {
        onSuccess: () => {
          setCreatorMessageText('');
          resetPendingAttachments();
          setInterceptOpen(false);
        },
        onError: (error) => {
          raiseFlashMessage({
            status: 'error',
            message: translate(
              error.message || 'Failed to request edits. Please try again.'
            ),
          });
        },
      }
    );
  };

  const handleInterceptSendAsMessage = (dontAskAgain: boolean) => {
    persistDontAskAgain(dontAskAgain);
    setInterceptOpen(false);
    executeSendCreatorMessage();
  };

  return (
    <Box className={sharedStyles.container} data-testid={testID}>
      <Box className={styles.header}>
        <Headline size="xs" className={sharedStyles.title}>
          {translate('Activity feed')}
        </Headline>
        <SegmentedControl
          segments={segments}
          selectedSegmentId={activeTab}
          onChange={(tab) => {
            onActivityTabChange?.(tab);
            setActiveTab(tab);
          }}
          fullWidthSegments
          classes={{control: styles.compactToggle}}
        />
      </Box>
      <Box className={styles.tabContent}>
        {activeTab === TAB_CREATOR ? (
          <>
            {creatorProfileId ? (
              <Stack
                component="button"
                type="button"
                direction="row"
                alignItems="center"
                gap={1}
                className={styles.allMessagesLink}
                onClick={() => openMessagingPanel(creatorProfileId)}
              >
                <Icon
                  size="small"
                  appearance="secondary"
                  name="Actions-Arrow-Back-Small"
                />
                <Body
                  size="md"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  {translate('all_messages_with', {
                    creatorName: creatorFirstName,
                  })}
                </Body>
              </Stack>
            ) : null}
            <Box
              ref={creatorFeedListRef}
              className={clsx(
                sharedStyles.feedList,
                showCreatorThreadEmpty && styles.feedListWithCenteredEmpty
              )}
            >
              {showCreatorThreadEmpty ? (
                <Body size="sm" className={styles.creatorThreadEmpty}>
                  {translate('No messages on this asset yet.')}
                </Body>
              ) : null}
              {creatorFeedItems.map((item) => (
                <FeedItemView key={item.id} item={item} />
              ))}
            </Box>
            <Box className={styles.commentInputArea}>
              <CommentInput
                value={creatorMessageText}
                onChange={setCreatorMessageText}
                onSubmit={handleCreatorMessageSubmit}
                isSubmitting={sendVersionMessage.isLoading}
                placeholder={
                  isLatestVersion
                    ? translate('Message about this asset...')
                    : translate('Message creator on latest version...')
                }
                attachments={{
                  pending: pendingAttachments,
                  isUploading,
                  onFileChange: handleFileChange,
                  onRemoveAttachment: handleRemoveAttachment,
                }}
              />
              {brandName ? (
                <Body
                  size="xs"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  {`${translate('Messaging as')} `}
                  <Body
                    component="span"
                    size="xs"
                    className={styles.commenterName}
                  >
                    {brandName}
                  </Body>
                </Body>
              ) : null}
            </Box>
          </>
        ) : (
          <>
            <Box ref={internalFeedListRef} className={sharedStyles.feedList}>
              {feedItems.length === 0 ? (
                <Body size="sm" className={styles.emptyState}>
                  {translate('No activity yet.')}
                </Body>
              ) : (
                feedItems.map((item) => (
                  <FeedItemView key={item.id} item={item} />
                ))
              )}
            </Box>
            <Box className={styles.commentInputArea}>
              <CommentInput
                value={commentText}
                onChange={setCommentText}
                onSubmit={handleSubmitComment}
                isSubmitting={isSubmitting}
                placeholder={
                  isLatestVersion
                    ? translate('Add a comment...')
                    : translate('Add a comment on latest version...')
                }
              />
              {commenterName ? (
                <Body
                  size="xs"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  {`${translate('Commenting as')} `}
                  <Body
                    component="span"
                    size="xs"
                    className={styles.commenterName}
                  >
                    {commenterName}
                  </Body>
                </Body>
              ) : null}
            </Box>
          </>
        )}
      </Box>
      <RequestEditsInterceptModal
        open={interceptOpen}
        messagePreview={creatorMessageText.trim()}
        attachmentCount={pendingAttachments.length}
        isLoading={
          isRejecting ||
          sendVersionMessage.isLoading ||
          isUploading ||
          isSendingRef.current
        }
        onRequestEdits={handleInterceptRequestEdits}
        onSendAsMessage={handleInterceptSendAsMessage}
        onCancel={() => setInterceptOpen(false)}
      />
      <ConfirmModal
        open={confirmSendOpen}
        title={translate(
          creatorName ? 'send-to-creator' : 'send-to-creator-generic',
          {name: creatorName}
        )}
        body="This message will be sent externally to the creator and cannot be undone."
        confirmLabel="Send to Creator"
        cancelLabel="Cancel"
        confirmAppearance="neutral"
        isLoading={
          sendVersionMessage.isLoading || isUploading || isSendingRef.current
        }
        isCancelDisabled={
          sendVersionMessage.isLoading || isUploading || isSendingRef.current
        }
        onConfirm={executeSendCreatorMessage}
        onCancel={() => setConfirmSendOpen(false)}
      >
        <div className={styles.confirmPreview}>
          <Body size="md" className={styles.confirmPreviewText}>
            {creatorMessageText.trim()}
          </Body>
          {pendingAttachments.length > 0 ? (
            <Body size="sm" className={styles.confirmPreviewMeta}>
              {translate('attachment-count', {
                count: pendingAttachments.length,
              })}
            </Body>
          ) : null}
        </div>
      </ConfirmModal>
    </Box>
  );
}

export default OnPlatformActivityFeed;
